import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import * as R from 'ramda';
import { CustomError, NetworkError, TechnicalError } from '../../common/errors';
import Result from '../../common/result';
import { Resolver, SIPServiceConfig } from '../../config';
import { Token, TokenResult } from '../common/token';
import SecureIdentityProvider, {
  CreateScopeRequest,
  ScopeRequestResult,
  DeepLinkResult,
  QrCodeResult,
  ScopeRequestStatusResult,
  ResolveScopeRequest,
  UploadEvidenceRequest,
  UploadEvidenceResult,
} from './secureIdentityProvider';
import { QrCodeTheme, ScopeRequest, ScopeRequestStatus } from './types';
import EvidenceService from '../credential/evidenceService';

export default class SecureIdentityProviderImplementation implements SecureIdentityProvider {
  protected config: SIPServiceConfig;

  private token?: Token;

  private defaultRetries: number;

  constructor() {
    const serviceConfig = Resolver.getServiceConfig();
    this.config = serviceConfig.sip;
    this.defaultRetries = serviceConfig.networkRetryConfig.defaultRetries;
  }

  setToken(token: Token): void {
    this.token = token;
  }

  private async getToken(): Promise<TokenResult> {
    if (!this.token) {
      return Result.failed(new TechnicalError('Missing auth token'));
    }

    return this.token.getToken();
  }

  private static getErrorResult<TSuccess = boolean>(url: string, error: unknown): Result<CustomError, TSuccess> {
    const statusCode = (error as AxiosError)?.response?.status;
    return Result.failed(new NetworkError(`Network error sip: ${url}`, statusCode || 500, error as Error));
  }

  async getScopeRequest(scopeRequestId: string): Promise<ScopeRequestResult> {
    const url = `${this.config.sipEndpoint}/${scopeRequestId}`;
    try {
      const authToken = await this.getToken();
      if (authToken.isFailed()) {
        return Result.failed(authToken.failure());
      }

      const response = await axios.request<ScopeRequest>({
        url,
        method: 'GET',
        headers: {
          Authorization: `Bearer ${authToken.success()}`,
        },
        retryConfig: {
          retries: this.defaultRetries,
        },
      });
      return Result.successful(response.data);
    } catch (error) {
      return SecureIdentityProviderImplementation.getErrorResult(url, error);
    }
  }

  async createScopeRequest(request: CreateScopeRequest): Promise<ScopeRequestResult> {
    try {
      const data = {
        app: {
          cpassPartnerId: request.cpassPartnerId, // Externally managed partner id from GKNS (e.g. Auth0 org)
          id: request.appId || this.config.defaultSipAppId, // SIP internal app ID (legacy, always use Civic one)
        },
        components: {
          identity: {
            request: {
              template: {
                reference: request.template,
                // only add the parameters if we have a non-default issuer
                ...(request.credentialsIssuerDid ? { parameters: { idvDid: request.credentialsIssuerDid } } : {}),
              },
            },
            ...(request.did ? { userId: request.did } : {}),
          },
        },
        wallet: request.wallet,
        timeout: request.timeout || this.config.defaultTimeout,
      };

      const response = await axios.request<ScopeRequest>({
        method: 'POST',
        url: this.config.sipEndpoint,
        data,
        headers: {
          'Content-Type': 'application/json',
        },
        retryConfig: {
          retries: 0,
          enableFailurePrompt: true,
        },
      });
      return Result.successful(response.data);
    } catch (error) {
      return SecureIdentityProviderImplementation.getErrorResult(this.config.sipEndpoint, error);
    }
  }

  getScopeRequestStatusEnum(value: string): ScopeRequestStatus | undefined {
    if (Object.values(ScopeRequestStatus).includes(value as ScopeRequestStatus)) {
      return value as ScopeRequestStatus;
    }
    return undefined;
  }

  async getScopeRequestStatus(scopeRequestId: string): Promise<ScopeRequestStatusResult> {
    const url = `${this.config.sipEndpoint}/${scopeRequestId}/status`;
    try {
      const response = await axios.request<ScopeRequest>({
        url,
        method: 'GET',
        retryConfig: {
          retries: this.defaultRetries,
        },
      });
      return Result.successful(response.data.status);
    } catch (error) {
      // The SIP endpoint can return a status result with an http error code, if this happens then return the status value as a success result
      const scopeRequestStatus = (error as AxiosError)?.response?.data?.status;
      if (scopeRequestStatus && R.is(String, scopeRequestStatus) && !R.isEmpty(scopeRequestStatus)) {
        const statusEnum = this.getScopeRequestStatusEnum(scopeRequestStatus);
        if (statusEnum !== undefined) {
          return Result.successful(statusEnum);
        }
      }

      return SecureIdentityProviderImplementation.getErrorResult(url, error);
    }
  }

  async retrieveDeepLink(scopeRequestId: string, redirectUrl: string | null): Promise<DeepLinkResult> {
    const url = `${this.config.sipEndpoint}/${scopeRequestId}/deeplink`;
    try {
      const response = await axios.request({
        method: 'POST',
        url,
        headers: {
          'Content-Type': 'application/json',
        },
        data: JSON.stringify({ redirectUrl, showManualRedirect: true }),
        retryConfig: {
          retries: this.defaultRetries,
        },
      });
      return Result.successful(response.data.url);
    } catch (error) {
      return SecureIdentityProviderImplementation.getErrorResult(url, error);
    }
  }

  private getBodyForNetwork(themeColor?: QrCodeTheme): string {
    let body;
    switch (themeColor) {
      case QrCodeTheme.BLUE:
        body = {
          size: 'l',
          error_correction: 'H',
          data_pattern: 'ROUND',
          eye_pattern: 'CIRC_CIRC',
          data_gradient_style: 'None',
          data_gradient_start_color: '#346887',
          data_gradient_end_color: '#346887',
          eye_color_inner: '#346887',
          eye_color_outer: '#346887',
          background_color: '#FFFFFF',
          showLogo: true,
          logo: {
            url: 'https://pass.civic.com/static/images/civic-c.png',
            size: 14,
          },
        };
        break;
      case QrCodeTheme.DEFAULT:
      default:
        body = {
          size: 'l',
          error_correction: 'H',
          data_pattern: 'ROUND',
          eye_pattern: 'CIRC_CIRC',
          data_gradient_style: 'None',
          data_gradient_start_color: '#FF6B4E',
          data_gradient_end_color: '#FF6B4E',
          eye_color_inner: '#FF6B4E',
          eye_color_outer: '#FF6B4E',
          background_color: '#FFFFFF',
          showLogo: false,
        };
        break;
    }

    return JSON.stringify(body);
  }

  async retrieveQRCode(scopeRequestId: string, themeTemplate?: QrCodeTheme): Promise<QrCodeResult> {
    const url = `${this.config.sipEndpoint}/${scopeRequestId}/qrcode`;
    try {
      const data = this.getBodyForNetwork(themeTemplate);
      const response = await axios.request({
        method: 'POST',
        url,
        headers: {
          'Content-Type': 'application/json',
        },
        data,
        retryConfig: {
          retries: this.defaultRetries,
        },
      });
      return Result.successful(response.data.image);
    } catch (error) {
      return SecureIdentityProviderImplementation.getErrorResult(url, error);
    }
  }

  async resolveScopeRequest(request: ResolveScopeRequest): Promise<ScopeRequestResult> {
    const url = `${this.config.sipEndpoint}/${request.scopeRequestId}`;
    try {
      const authToken = await this.getToken();
      if (authToken.isFailed()) {
        return Result.failed(authToken.failure());
      }

      const response = await axios.request({
        method: 'PATCH',
        url,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken.success()}`,
        },
        data: {
          components: request.components,
        },
        retryConfig: {
          enableFailurePrompt: true,
        },
      });
      return Result.successful(response.data);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError;
        const statusCode = axiosError.response?.status;
        if (statusCode === 412) {
          // This is the 'verification-failed' case, which is not an internal technical error.
          // The CredentialShareContext will detect this and set the status appropriately.
          return Result.successful(axiosError.response?.data);
        }
      }
      // For all other errors, treat as an internal technical error (Something Went Wrong).
      return SecureIdentityProviderImplementation.getErrorResult(url, error);
    }
  }

  async uploadEvidence(request: UploadEvidenceRequest): Promise<UploadEvidenceResult> {
    const { evidenceToUpload, evidenceChannel } = request;

    if (R.toLower(evidenceChannel.method || '') !== 'put') {
      throw new TechnicalError(
        `Unsupported evidence channel method for scope request ${request.scopeRequestId}. Expected: put, received: ${evidenceChannel.method}.`,
      );
    }

    try {
      const evidencePayload = await EvidenceService.getInstance().downloadEvidence(evidenceToUpload);

      const axiosRequest = {
        method: 'PUT',
        url: evidenceChannel.url,
        headers: {
          'Content-Type': 'application/json',
        },
        retryConfig: {
          retries: this.defaultRetries,
        },
        data: {
          ...R.omit(['evidenceData'], evidencePayload),
          base64Encoded: Buffer.from(evidencePayload.evidenceData).toString('base64'),
        },
      } as AxiosRequestConfig;
      await axios.request(axiosRequest);

      return Result.noValueSuccess();
    } catch (error) {
      return SecureIdentityProviderImplementation.getErrorResult(`${evidenceToUpload.downloadUrl}`, error);
    }
  }
}
